<template>
  <div class="new-campaign-wizard" @keydown.enter="handleEnterKey">
    <b-overlay :show="show" rounded="sm" class="wiz-overlay">

      <!-- ── Step indicator ──────────────────────────────────── -->
      <div class="wiz-steps">
        <div
          v-for="(item, idx) in visibleSteps"
          :key="item.step"
          class="wiz-step"
          :class="{
            'wiz-step--active': currentStep === item.step,
            'wiz-step--done':   currentStep > item.step,
            'wiz-step--clickable': currentStep > item.step
          }"
          @click="goToStep(item.step)"
        >
          <div class="wiz-step__num">
            <feather-icon v-if="currentStep > item.step" icon="CheckIcon" size="12" />
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <span class="wiz-step__label">{{ item.label }}</span>
        </div>
      </div>

      <!-- ── PASO 1: Identidad visual ───────────────────────── -->
      <div v-if="currentStep === 1" class="wiz-panel">

        <!-- Cover drag-drop zone -->
        <div
          class="cover-zone"
          :class="{
            'cover-zone--over':   isDragOverCover,
            'cover-zone--filled': !!coverPreview
          }"
          @dragover.prevent="isDragOverCover = true"
          @dragleave.prevent="isDragOverCover = false"
          @drop.prevent="onDropCover($event)"
          @click="$refs.coverInput.click()"
        >
          <img v-if="coverPreview" :src="coverPreview" class="cover-zone__img" alt="cover preview" />
          <div v-else class="cover-zone__hint">
            <feather-icon icon="ImageIcon" size="28" class="text-muted" />
            <p class="mt-50 mb-25 text-muted font-small-3">
              Arrastra el cover aquí o
              <b-link class="font-small-3" @click.stop="$refs.coverInput.click()">selecciona un archivo</b-link>
            </p>
            <small class="text-muted">JPG, PNG · recomendado 1200 × 400 px</small>
          </div>
          <b-button
            v-if="coverPreview"
            size="sm"
            variant="light"
            class="cover-zone__change"
            @click.stop="$refs.coverInput.click()"
          >
            <feather-icon icon="Edit2Icon" size="12" class="mr-25" />{{ $t('campaigns.msjChange') }}
          </b-button>
          <input
            ref="coverInput"
            type="file"
            accept=".jpeg,.jpg,.png"
            class="d-none"
            @change="onCoverChange"
          />
        </div>

        <!-- Logo circle overlay -->
        <div class="logo-wrap">
          <div class="logo-circle" @click="$refs.logoInput.click()">
            <img v-if="logoPreview" :src="logoPreview" class="logo-circle__img" alt="logo preview" />
            <div v-else class="logo-circle__empty">
              <feather-icon icon="CameraIcon" size="22" class="text-muted" />
            </div>
            <div class="logo-circle__over">
              <feather-icon icon="Edit2Icon" size="14" />
            </div>
          </div>
          <small class="text-muted d-block text-center mt-50">{{ $t('campaigns.uploadLogo') }}</small>
          <input
            ref="logoInput"
            type="file"
            accept=".jpeg,.jpg,.png"
            class="d-none"
            @change="onLogoChange"
          />
        </div>

        <!-- Campaign name -->
        <div class="mb-1">
          <h6 class="wiz-label">{{ $t('campaigns.campaignName') }}</h6>
          <b-input-group>
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="name"
              :placeholder="$t('campaigns.campaignName')"
              autocomplete="off"
              maxlength="100"
            />
          </b-input-group>
        </div>

        <!-- Brand + Budget (2 cols) -->
        <div :class="role !== 'realty' ? 'wiz-two-col' : ''">
          <div v-if="role !== 'realty'">
            <h6 class="wiz-label">{{ $t('campaigns.brand') }}</h6>
            <b-form-group class="mb-0">
              <v-select
                v-model="selectedBrand"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                label="title"
                :options="nameBrand"
                :append-to-body="true"
                :calculate-position="withPopper"
              />
            </b-form-group>
          </div>
          <div>
            <h6 class="wiz-label">{{ $t('campaigns.budget') }}</h6>
            <b-input-group prepend="$">
              <cleave
                v-model="budget"
                class="form-control"
                :raw="false"
                :options="options.number"
                placeholder="10,000"
                :maxlength="11"
                @input="validateBudget"
              />
            </b-input-group>
          </div>
        </div>
      </div>

      <!-- ── PASO 2: Canal de YouTube ──────────────────────── -->
      <div v-if="currentStep === 2 && can_use_youtube" class="wiz-panel">
        <h6 class="wiz-label mb-50">Canal de YouTube</h6>
        <p class="text-muted font-small-3 mb-1">Selecciona la cuenta de YouTube de la marca para esta campaña.</p>

        <div v-if="loadingYoutubeNetworks" class="text-center py-3">
          <b-spinner small variant="primary" />
        </div>

        <div v-else-if="youtubeNetworks.length === 0">
          <!-- OAuth -->
          <div v-if="!addingManual">
            <b-button class="google-yt-btn w-100 mb-1" @click="youtubeLoginWizard()">
              <b-img :src="googleIcon" class="google-yt-btn__icon" />
              <span>Conectar con Google</span>
            </b-button>
            <p v-if="oauthOpened" class="text-muted font-small-3 text-center mb-1">
              Completa el proceso en la ventana que se abrió y después
              <b-link @click="refreshYoutubeNetworks()">recarga las cuentas</b-link>.
            </p>

            <div class="yt-divider my-1"><span>o</span></div>

            <b-button variant="outline-secondary" class="w-100" @click="addingManual = true">
              <feather-icon icon="EditIcon" size="14" class="mr-50" />
              Ingresar manualmente
            </b-button>
          </div>

          <!-- Formulario manual -->
          <div v-else>
            <b-button variant="flat-secondary" size="sm" class="mb-1 p-0" @click="addingManual = false; youtubeChannelError = null">
              <feather-icon icon="ChevronLeftIcon" size="14" /> Volver
            </b-button>
            <div class="mb-1">
              <h6 class="wiz-label">Handle de YouTube</h6>
              <b-input-group prepend="@">
                <b-form-input
                  v-model="manualYoutube.username"
                  placeholder="micanal"
                  maxlength="100"
                  :state="youtubeChannelError ? false : null"
                  @input="youtubeChannelError = null"
                />
              </b-input-group>
            </div>
            <div class="mb-1">
              <h6 class="wiz-label">Suscriptores</h6>
              <cleave
                v-model="manualYoutube.followers"
                class="form-control"
                :raw="false"
                :options="options.number"
                placeholder="10,000"
              />
            </div>
            <b-alert v-if="youtubeChannelError" show variant="danger" class="mb-1 py-75 px-1 font-small-3">
              <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
              {{ youtubeChannelError }}
            </b-alert>
            <b-button
              variant="primary"
              class="w-100"
              :disabled="!manualYoutube.username || !manualYoutube.followers || savingManual"
              @click="addYoutubeManual()"
            >
              <b-spinner v-if="savingManual" small class="mr-50" />
              Agregar cuenta
            </b-button>
          </div>
        </div>

        <div v-else>
          <!-- Lista de cuentas -->
          <div v-if="!addingManual" class="youtube-networks-list">
            <div
              v-for="network in youtubeNetworks"
              :key="network.uuid"
              class="youtube-network-card"
              :class="{ 'youtube-network-card--selected': selectedYoutubeNetwork === network.uuid }"
              @click="selectedYoutubeNetwork = selectedYoutubeNetwork === network.uuid ? null : network.uuid"
            >
              <b-avatar size="38" class="mr-1">
                <b-img :src="youtubeIcon" class="padding-07 youtube-border" fluid />
              </b-avatar>
              <div class="text-left overflow-hidden">
                <strong class="d-block text-truncate">{{ network.username || network.name || 'YouTube' }}</strong>
                <small class="text-muted text-truncate d-block">{{ network.url || network.profile_url || '' }}</small>
              </div>
              <feather-icon
                v-if="selectedYoutubeNetwork === network.uuid"
                icon="CheckCircleIcon"
                size="18"
                class="ml-auto text-success flex-shrink-0"
              />
            </div>

            <!-- Botón agregar nueva cuenta -->
            <div class="yt-divider mt-2 mb-2"><span>agregar otra cuenta</span></div>
            <div class="d-flex" style="gap:10px">
              <b-button class="google-yt-btn" style="flex:1" @click="youtubeLoginWizard()">
                <b-img :src="googleIcon" class="google-yt-btn__icon" />
                <span>Google</span>
              </b-button>
              <b-button variant="outline-secondary" style="flex:1" @click="addingManual = true">
                <feather-icon icon="EditIcon" size="14" class="mr-50" />
                Manual
              </b-button>
            </div>
            <p v-if="oauthOpened" class="text-muted font-small-3 text-center mt-50 mb-0">
              Completa el proceso y después
              <b-link @click="refreshYoutubeNetworks()">recarga las cuentas</b-link>.
            </p>
          </div>

          <!-- Formulario manual (desde lista con cuentas) -->
          <div v-else>
            <b-button variant="flat-secondary" size="sm" class="mb-1 p-0" @click="addingManual = false; youtubeChannelError = null">
              <feather-icon icon="ChevronLeftIcon" size="14" /> Volver
            </b-button>
            <div class="mb-1">
              <h6 class="wiz-label">Handle de YouTube</h6>
              <b-input-group prepend="@">
                <b-form-input
                  v-model="manualYoutube.username"
                  placeholder="micanal"
                  maxlength="100"
                  :state="youtubeChannelError ? false : null"
                  @input="youtubeChannelError = null"
                />
              </b-input-group>
            </div>
            <div class="mb-1">
              <h6 class="wiz-label">Suscriptores</h6>
              <cleave
                v-model="manualYoutube.followers"
                class="form-control"
                :raw="false"
                :options="options.number"
                placeholder="10,000"
              />
            </div>
            <b-alert v-if="youtubeChannelError" show variant="danger" class="mb-1 py-75 px-1 font-small-3">
              <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
              {{ youtubeChannelError }}
            </b-alert>
            <b-button
              variant="primary"
              class="w-100"
              :disabled="!manualYoutube.username || !manualYoutube.followers || savingManual"
              @click="addYoutubeManual()"
            >
              <b-spinner v-if="savingManual" small class="mr-50" />
              Agregar cuenta
            </b-button>
          </div>
        </div>
      </div>

      <!-- ── PASO 3: Datos de campaña ───────────────────────── -->
      <div v-if="currentStep === 3" class="wiz-panel">
        <b-alert v-if="youtubeRecommendation && can_use_youtube" show variant="warning" class="mb-2 font-small-3 p-75">
          <feather-icon icon="YoutubeIcon" size="14" class="mr-50" />
          <strong>Recomendación:</strong> vincula tu cuenta de YouTube para conectar con la API y obtener métricas precisas de cada contenido publicado.
          <b-link class="alert-link ml-50" @click="currentStep = 2">Vincular ahora</b-link>
        </b-alert>
        <div class="wiz-two-col mb-1">
          <div>
            <h6 class="wiz-label">{{ $t('campaigns.initDate') }}</h6>
            <b-form-datepicker
              id="fecha-inicio"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              v-model="init_date"
              class="mb-1"
              :placeholder="$t('campaigns.initDate')"
            />
          </div>
          <div>
            <h6 class="wiz-label">{{ $t('campaigns.endDate') }}</h6>
            <b-form-datepicker
              id="fecha-final"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              v-model="end_date"
              class="mb-1"
              :placeholder="$t('campaigns.endDate')"
            />
          </div>
        </div>
        <div>
          <h6 class="wiz-label">{{ $t('campaigns.description') }}</h6>
          <b-form-group>
            <component
              :is="quill_editor"
              id="quil-content"
              v-model="description"
              :options="editorOption[0]"
              class="border-bottom-0 wiz-quill"
            />
            <div id="quill-toolbar0" class="d-flex justify-content-end border-top-0">
              <button class="ql-bold" />
              <button class="ql-italic" />
              <button class="ql-underline" />
              <button class="ql-align" />
              <button class="ql-link" />
            </div>
          </b-form-group>
        </div>
        <div class="mb-1">
          <h6 class="wiz-label">{{ $t('campaigns.promotionUrl') }}</h6>
          <b-form-input
            v-model="promotion_url"
            type="url"
            :placeholder="$t('campaigns.promotionUrlPlaceholder')"
            :state="urlValidationState"
          />
          <b-form-invalid-feedback :state="urlValidationState">
            {{ $t('campaigns.aUrlText') }}
          </b-form-invalid-feedback>
        </div>
      </div>

      <!-- ── PASO 4: Mensaje y requisitos ───────────────────── -->
      <div v-if="currentStep === 4" class="wiz-panel">
        <b-alert v-if="youtubeRecommendation && can_use_youtube" show variant="warning" class="mb-2 font-small-3 p-75">
          <feather-icon icon="YoutubeIcon" size="14" class="mr-50" />
          <strong>Recomendación:</strong> vincula tu cuenta de YouTube para conectar con la API y obtener métricas precisas de cada contenido publicado.
          <b-link class="alert-link ml-50" @click="currentStep = 2">Vincular ahora</b-link>
        </b-alert>
        <div v-if="role !== 'realty'" class="mb-2">
          <h6 class="wiz-label">{{ $t('campaigns.message') }}</h6>
          <b-form-checkbox
            v-model="showCustomMessage"
            class="custom-control-primary text-left mt-1 mb-1"
          >
            {{ $t('campaigns.showMessage') }}
          </b-form-checkbox>
          <b-form-group>
            <component
              :is="quill_editor"
              id="quil-content-msg"
              v-model="message"
              :options="editorOption[1]"
              class="border-bottom-0 wiz-quill"
            />
            <div id="quill-toolbar1" class="d-flex justify-content-end border-top-0">
              <button class="ql-bold" />
              <button class="ql-italic" />
              <button class="ql-underline" />
              <button class="ql-align" />
              <button class="ql-link" />
            </div>
          </b-form-group>
        </div>

        <div
          v-for="(item, index) in guidelines"
          :key="index"
          class="wiz-guideline mb-1"
        >
          <div class="d-flex align-items-center">
            <h6 class="mb-0 font-weight-bolder">{{ item.front_name }}</h6>
            <div class="ml-auto d-flex">
              <b-button
                variant="flat-primary"
                class="btn-icon"
                @click="editGuideline(index)"
              >
                <feather-icon icon="Edit2Icon" />
              </b-button>
              <b-button
                variant="flat-danger"
                class="btn-icon"
                @click="deleteGuideline(index)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </div>
          <b-form-checkbox
            :checked="item.show_link"
            :disabled="true"
            class="custom-control-primary text-left mt-1 mb-1"
          >
            {{ $t('campaigns.showMessage') }}
          </b-form-checkbox>
          <b-form-group>
            <div v-html="item.form_value"></div>
          </b-form-group>
        </div>

        <div class="d-flex justify-content-center mt-1 mb-1">
          <b-button variant="success" @click="showModalAddCustom()">
            {{ $t('campaigns.addRequirement') }}
          </b-button>
        </div>
      </div>

      <!-- ── Navigation footer ──────────────────────────────── -->
      <div v-if="!(currentStep === 2 && addingManual)" class="wiz-footer">
        <b-button
          v-if="currentStep > 1"
          variant="outline-secondary"
          @click="prevStep()"
        >
          <feather-icon icon="ChevronLeftIcon" size="14" /> Atrás
        </b-button>
        <div v-else />

        <div class="wiz-dots">
          <span
            v-for="s in visibleSteps.map(item => item.step)"
            :key="s"
            class="wiz-dot"
            :class="{
              'wiz-dot--active': currentStep === s,
              'wiz-dot--done':   currentStep > s
            }"
          />
        </div>

        <b-button
          v-if="currentStep < 4"
          variant="primary"
          :disabled="currentStep === 2 && !!youtubeChannelError"
          @click="validateAndNext()"
        >
          Siguiente <feather-icon icon="ChevronRightIcon" size="14" />
        </b-button>
        <b-button
          v-else
          variant="success"
          @click="createdList()"
          :disabled="creatingCampaign"
        >
          {{ $t('campaigns.createCampaign') }}
        </b-button>
      </div>

      <!-- Requirement modal (stays inside component) -->
      <modal-brandme size="xl" :show_modal="show_modal" :key="change_modal">
        <template #title>{{ $t('campaigns.newRequirement') }}</template>
        <template #body>
          <new-requirement
            :campaign_uuid="$route.params.uuid"
            :editable_data="editingGuideline"
            @closeModal="closeModalCustomField"
            @dataCustomField="dataCustomField"
          />
        </template>
      </modal-brandme>

    </b-overlay>
  </div>
</template>

<script>
import {
  BLink, BInputGroup, BFormInput, BInputGroupPrepend, BFormDatepicker,
  BButton, BFormGroup, BFormCheckbox, BOverlay, BSpinner, BAvatar, BImg, BAlert
} from 'bootstrap-vue'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import Cleave from 'vue-cleave-component'
import vSelect from 'vue-select'
import { createPopper } from '@popperjs/core'
const loadToastificationContent = () => import('@core/components/toastification/ToastificationContent.vue')
import service from '@/services/campaign'
import othersService from '@/services/others'
import { hasPermission } from '@/libs/utils/user'
import { getIconWhite, showToast } from '@/libs/utils/icons'
import { getUsernameUrlYoutube } from '@/libs/utils/urls'
import ModalBrandme from '../components/modal/ModalBrandme.vue'
import newRequirement from '../campaings/newRequirement.vue'

export default {
  components: {
    BLink,
    BInputGroup,
    BFormInput,
    BInputGroupPrepend,
    BFormDatepicker,
    BButton,
    BFormGroup,
    Cleave,
    vSelect,
    BFormCheckbox,
    BOverlay,
    BSpinner,
    BAvatar,
    BImg,
    BAlert,
    ModalBrandme,
    newRequirement
  },
  mixins: [togglePasswordVisibility],
  data () {
    return {
      hasPermission,
      showToast,
      // Wizard
      currentStep: 1,
      stepLabels: ['Identidad visual', 'Canal de YouTube', 'Datos de campaña', 'Mensaje y requisitos'],
      // YouTube step
      youtubeNetworks: [],
      selectedYoutubeNetwork: null,
      loadingYoutubeNetworks: false,
      youtubeIcon: getIconWhite('youtube'),
      googleIcon: require('@/assets/images/google/google.svg'),
      addingManual: false,
      oauthOpened: false,
      youtubeRecommendation: false,
      savingManual: false,
      manualYoutube: { username: '', followers: '' },
      youtubeChannelError: null,
      // Image upload
      coverPreview: null,
      logoPreview: null,
      isDragOverCover: false,
      // Form
      show: false,
      init_date: '',
      end_date: '',
      description: '',
      options: {
        number: {
          numeral: true,
          numeralThousandsGroupStyle: 'thousand'
        }
      },
      logo: '',
      cover: '',
      name: '',
      brand: '',
      selectedBrand: { title: this.$t('campaigns.selectBrand'), uuid: '' },
      nameBrand: [],
      budget: '',
      promotion_url: '',
      customMessage: '',
      showCustomMessage: true,
      message: '',
      creatingCampaign: this.disabledAddCampaign(),
      role: 'brand',
      show_modal: false,
      change_modal: true,
      guidelines: [],
      editingGuideline: null,
      editingGuidelineIndex: null,
      quill_editor: null,
    }
  },
  created () {
    const data_user = JSON.parse(localStorage.getItem('userData'))
    this.role = data_user.main_group.name
    this.getBrands()
    this.getMessageInvitation()
  },
  async mounted () {
    const vue_quill_editor = (await import('vue-quill-editor'))
    this.quill_editor = vue_quill_editor.quillEditor
    await import('quill/dist/quill.core.css')
    await import('quill/dist/quill.snow.css')
    await import('quill/dist/quill.bubble.css')
  },
  setup (props, context) {
    const j = 2
    let i = 0
    const editorOption = []
    do {
      const value = {
        modules: { toolbar: `#quill-toolbar${i}` },
        placeholder: context.parent.$t('campaigns.writeHere')
      }
      editorOption.push(value)
      i += 1
    } while (i < j)
    return { editorOption }
  },
  computed: {
    // Solo usuarios con google_ycp pueden vincular el canal de YouTube a la campaña
    can_use_youtube () {
      return hasPermission('google_ycp')
    },
    visibleSteps () {
      return this.stepLabels
        .map((label, idx) => ({ step: idx + 1, label }))
        .filter(item => item.step !== 2 || this.can_use_youtube)
    },
    urlValidationState () {
      if (!this.promotion_url) return null
      try { new URL(this.promotion_url); return true } catch { return false }
    },
    passwordToggleIcon () {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    userData () {
      return JSON.parse(localStorage.getItem('userData'))
    },
    nameUser () {
      return `${this.userData.first_name} ${this.userData.last_name}`
    },
    is_free () {
      return this.userData.current_plan.name === 'Free'
    }
  },
  methods: {
    // ── v-select popper (append-to-body) ───────────────────
    withPopper (dropdownList, component, { width }) {
      dropdownList.style.width = width
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom',
        modifiers: [
          { name: 'offset', options: { offset: [0, -1] } },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            }
          }
        ]
      })
      return () => popper.destroy()
    },

    // ── Image upload handlers ───────────────────────────────
    onCoverChange () {
      const file = this.$refs.coverInput.files[0]
      if (file) {
        this.cover = file
        this.coverPreview = URL.createObjectURL(file)
      }
    },
    onLogoChange () {
      const file = this.$refs.logoInput.files[0]
      if (file) {
        this.logo = file
        this.logoPreview = URL.createObjectURL(file)
      }
    },
    onDropCover (event) {
      this.isDragOverCover = false
      const file = event.dataTransfer.files[0]
      if (file && /\.(jpe?g|png)$/i.test(file.name)) {
        this.cover = file
        this.coverPreview = URL.createObjectURL(file)
      }
    },

    // ── Wizard navigation ───────────────────────────────────
    goToStep (step) {
      // Solo permite regresar a pasos ya completados
      if (step < this.currentStep) {
        this.currentStep = step
      }
    },
    validateAndNext () {
      if (this.currentStep === 1) {
        if (!this.cover) {
          return showToast(this, this.$t('campaigns.aCover'), this.$t('campaigns.aCoverText'), 'AlertCircleIcon')
        }
        if (!this.logo) {
          return showToast(this, this.$t('campaigns.aLogo'), this.$t('campaigns.aLogoText'), 'AlertCircleIcon')
        }
        if (!this.name) {
          return showToast(this, this.$t('campaigns.aName'), this.$t('campaigns.aNameText'), 'AlertCircleIcon')
        }
        if (!this.budget) {
          return showToast(this, this.$t('campaigns.aPrice'), this.$t('campaigns.aPriceText'), 'AlertCircleIcon')
        }
      }
      if (this.currentStep === 2 && this.can_use_youtube && !this.selectedYoutubeNetwork) {
        this.showYoutubeWarning()
        return
      }
      if (this.currentStep === 2 && this.selectedYoutubeNetwork) {
        this.youtubeRecommendation = false
      }
      if (this.currentStep === 3) {
        if (!this.description) {
          return showToast(this, this.$t('campaigns.aDescription'), this.$t('campaigns.aDescriptionText'), 'AlertCircleIcon')
        }
        if (!this.init_date) {
          return showToast(this, this.$t('campaigns.aInit'), this.$t('campaigns.aInitText'), 'AlertCircleIcon')
        }
        if (!this.end_date) {
          return showToast(this, this.$t('campaigns.aEnd'), this.$t('campaigns.aEndText'), 'AlertCircleIcon')
        }
        if (this.promotion_url) {
          try {
            new URL(this.promotion_url)
          } catch {
            return showToast(this, this.$t('campaigns.aUrl'), this.$t('campaigns.aUrlText'), 'AlertCircleIcon')
          }
        }
      }
      this.nextStep()
    },
    nextStep () {
      const steps = this.visibleSteps.map(item => item.step)
      this.currentStep = steps[steps.indexOf(this.currentStep) + 1] || this.currentStep
      if (this.currentStep === 2) {
        this.fetchYoutubeNetworks()
      }
    },
    prevStep () {
      const steps = this.visibleSteps.map(item => item.step)
      this.currentStep = steps[steps.indexOf(this.currentStep) - 1] || this.currentStep
    },

    // ── Form helpers ────────────────────────────────────────
    validateBudget () {
      const maxBudget = 999999999
      const enteredBudget = parseInt(this.budget.replace(/,/g, ''), 10)
      if (enteredBudget > maxBudget) {
        this.budget = '999,999,999'
      }
    },
    formatDate (date) {
      const fecha = date.split('-')
      return parseInt(fecha[1]) > 12 ? `${fecha[0]}-${fecha[2]}-${fecha[1]}` : `${fecha[0]}-${fecha[1]}-${fecha[2]}`
    },
    deleteGuideline (index) {
      this.guidelines.splice(index, 1)
    },
    dataCustomField (data) {
      const editingIndex = this.editingGuidelineIndex
      this.closeModalCustomField()
      if (editingIndex !== null) {
        this.$set(this.guidelines, editingIndex, data)
      } else {
        this.guidelines.push(data)
      }
    },
    editGuideline (index) {
      this.editingGuidelineIndex = index
      this.editingGuideline = { ...this.guidelines[index] }
      this.show_modal = true
      this.change_modal = !this.change_modal
    },
    closeModalCustomField () {
      this.show_modal = false
      this.change_modal = !this.change_modal
      this.editingGuideline = null
      this.editingGuidelineIndex = null
    },
    showModalAddCustom () {
      this.editingGuideline = null
      this.editingGuidelineIndex = null
      this.show_modal = true
      this.change_modal = !this.change_modal
    },
    getBrand (info) {
      return info.map(i => ({ title: i.name, uuid: i.uuid }))
    },

    // ── Submit ──────────────────────────────────────────────
    createdList () {
      if (this.disabledAddCampaign()) return

      if (this.can_use_youtube && !this.selectedYoutubeNetwork) {
        this.showYoutubeWarningOnCreate()
        return
      }

      this.doCreateCampaign()
    },
    showYoutubeWarningOnCreate () {
      const escHandler = (e) => {
        if (e.key === 'Escape' && this.$swal.isVisible()) {
          e.stopImmediatePropagation()
          this.$swal.close()
        }
      }
      document.addEventListener('keydown', escHandler, true)

      this.$swal({
        title: '¿Crear sin YouTube?',
        html: 'Si planeas publicar contenido en YouTube, te recomendamos vincular tu cuenta para conectar con la API y obtener <strong>métricas precisas</strong> de cada publicación.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Crear campaña',
        cancelButtonText: 'Volver y vincular',
        customClass: {
          confirmButton: 'btn btn-success ml-1',
          cancelButton: 'btn btn-primary mr-1',
          actions: 'justify-content-center',
        },
        buttonsStyling: false,
        reverseButtons: true,
        heightAuto: false,
      }).then(result => {
        document.removeEventListener('keydown', escHandler, true)
        if (result.isConfirmed) {
          this.doCreateCampaign()
        } else if (result.isDismissed && result.dismiss === 'cancel') {
          this.currentStep = 2
        }
      })
    },
    doCreateCampaign () {
      const brand = this.selectedBrand?.uuid || ''

      if (!this.description) {
        return showToast(this, this.$t('campaigns.aDescription'), this.$t('campaigns.aDescriptionText'), 'AlertCircleIcon')
      }
      if (!this.cover) {
        return showToast(this, this.$t('campaigns.aCover'), this.$t('campaigns.aCoverText'), 'AlertCircleIcon')
      }
      if (!this.logo) {
        return showToast(this, this.$t('campaigns.aLogo'), this.$t('campaigns.aLogoText'), 'AlertCircleIcon')
      }
      if (!this.name) {
        return showToast(this, this.$t('campaigns.aName'), this.$t('campaigns.aNameText'), 'AlertCircleIcon')
      }
      if (!this.budget) {
        return showToast(this, this.$t('campaigns.aPrice'), this.$t('campaigns.aPriceText'), 'AlertCircleIcon')
      }

      if (!this.init_date) {
        this.creatingCampaign = false
        return showToast(this, this.$t('campaigns.aInit'), this.$t('campaigns.aInitText'), 'AlertCircleIcon')
      } else {
        this.init_date = this.formatDate(this.init_date)
        this.creatingCampaign = false
      }

      const dateInit = /^\d{4}-\d{2}-\d{2}$/
      if (!dateInit.test(this.init_date)) {
        this.creatingCampaign = false
        return showToast(this, this.$t('campaigns.invalidFormat'), this.$t('campaigns.invalidFormatText'), 'AlertCircleIcon')
      }

      if (!this.end_date) {
        this.creatingCampaign = false
        return showToast(this, this.$t('campaigns.aEnd'), this.$t('campaigns.aEndText'), 'AlertCircleIcon')
      } else {
        this.end_date = this.formatDate(this.end_date)
        this.creatingCampaign = false
      }

      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (!dateRegex.test(this.end_date)) {
        this.creatingCampaign = false
        return showToast(this, this.$t('campaigns.invalidFormat'), this.$t('campaigns.invalidFormatText'), 'AlertCircleIcon')
      }

      if (!this.message) {
        this.creatingCampaign = false
        loadToastificationContent().then((component) => {
          this.$toast({
            component: component.default,
            position: 'top-center',
            props: {
              title: this.$t('campaigns.aMessage'),
              icon: 'AlertCircleIcon',
              text: this.$t('campaigns.aMessageText'),
              variant: 'danger'
            }
          })
        })
        return false
      }

      const custom_fields = JSON.stringify(JSON.stringify(this.guidelines))
      this.show = true
      const precio = this.budget.replace(/,/g, '')
      const formData = new FormData()
      formData.append('cover', this.cover)
      formData.append('logo', this.logo)
      formData.set('name', this.name)
      formData.set('brand', brand)
      formData.set('budget', precio)
      formData.set('init_date', this.init_date)
      formData.set('end_date', this.end_date)
      formData.set('description', this.description)
      formData.set('url', this.promotion_url)
      formData.set('brand_reference', brand)
      formData.set('custom_fields', custom_fields)
      if (this.selectedYoutubeNetwork) {
        const youtubeNetwork = this.youtubeNetworks.find(n => n.uuid === this.selectedYoutubeNetwork)
        if (youtubeNetwork?.username) {
          formData.set('channel_brand_youtube', youtubeNetwork.username)
        }
      }
      if (this.message !== '') {
        formData.set('show_custom_message', this.showCustomMessage)
        formData.set('custom_message', this.message)
      } else {
        formData.set('show_custom_message', false)
        formData.set('custom_message', '')
      }

      service.createCampaign(formData)
        .then(result => {
          this.show = false
          this.listCreatedConfirmation(result)
          this.creatingCampaign = false
          if (this.is_free) this.$emit('update_campaigns_avaible')
        })
        .catch(() => {
          this.show = false
          console.log('FAILURE!!')
          this.creatingCampaign = false
        })
    },
    resetForm () {
      if (this.$refs.coverInput) this.$refs.coverInput.value = ''
      if (this.$refs.logoInput) this.$refs.logoInput.value = ''
      this.cover = ''
      this.logo = ''
      this.coverPreview = null
      this.logoPreview = null
      this.name = ''
      this.description = ''
      this.budget = ''
      this.init_date = ''
      this.end_date = ''
      this.selectedBrand = { title: this.$t('campaigns.selectBrand'), uuid: '' }
      this.youtubeNetworks = []
      this.selectedYoutubeNetwork = null
      this.addingManual = false
      this.oauthOpened = false
      this.youtubeRecommendation = false
      this.manualYoutube = { username: '', followers: '' }
      this.currentStep = 1
      this.getMessageInvitation()
    },
    listCreatedConfirmation (campaign) {
      if (campaign && campaign.uuid) {
        this.$emit('campaignCreated', campaign)
        this.$swal({
          title: this.$t('campaigns.aCreated'),
          text: this.$t('campaigns.aCreatedText'),
          icon: 'success',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
          heightAuto: false,
        }).then(() => {
          this.resetForm()
        })
      } else {
        this.$emit('campaignCreated', campaign)
        this.$swal({
          title: this.$t('campaigns.errorCreated'),
          text: this.$t('campaigns.errorCreatedText'),
          variant: 'danger',
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
          heightAuto: false,
        }).then(() => {
          this.resetForm()
        })
      }
    },
    acceptListCreated () {
      location.reload()
    },
    getMessageInvitation () {
      service.getMessageInvitation()
        .then(response => {
          this.message = response.message
        })
    },
    getBrands () {
      // Gabo
      service.getCampaignBrands()
        .then(response => {
          response.results.map((item) => {
            this.nameBrand.push({ title: item.name, uuid: item.uuid })
          })
        })
        .catch((error) => {
          console.log(error)
        })
    },
    showYoutubeWarning () {
      // Intercepta Escape en fase capture para que SweetAlert lo procese
      // antes de que Bootstrap-Vue cierre el modal
      const escHandler = (e) => {
        if (e.key === 'Escape' && this.$swal.isVisible()) {
          e.stopImmediatePropagation()
          this.$swal.close()
        }
      }
      document.addEventListener('keydown', escHandler, true)

      this.$swal({
        title: '¿Continuar sin YouTube?',
        html: 'Si planeas publicar contenido en YouTube, te recomendamos vincular tu cuenta para conectar con la API y obtener <strong>métricas precisas</strong> de cada publicación.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuar de todas formas',
        cancelButtonText: 'Volver y vincular',
        customClass: {
          confirmButton: 'btn btn-outline-secondary ml-1',
          cancelButton: 'btn btn-primary mr-1',
          actions: 'justify-content-center',
        },
        buttonsStyling: false,
        reverseButtons: true,
        heightAuto: false,
      }).then(result => {
        document.removeEventListener('keydown', escHandler, true)
        if (result.value) {
          this.youtubeRecommendation = true
          this.currentStep++
        }
      })
    },
    fetchYoutubeNetworks () {
      if (this.youtubeNetworks.length > 0) return
      this.loadingYoutubeNetworks = true
      othersService.getSocialNetworks('networks=youtube')
        .then(response => {
          this.youtubeNetworks = response.results || []
          if (this.youtubeNetworks.length === 1) {
            this.selectedYoutubeNetwork = this.youtubeNetworks[0].uuid
          }
        })
        .catch(() => {
          this.youtubeNetworks = []
        })
        .finally(() => {
          this.loadingYoutubeNetworks = false
        })
    },
    refreshYoutubeNetworks () {
      this.youtubeNetworks = []
      this.oauthOpened = false
      this.fetchYoutubeNetworks()
    },
    youtubeLoginWizard () {
      localStorage.setItem('network_code', 'youtube')
      const rootUrl = 'https://accounts.google.com/o/oauth2/v2/auth'
      const options = {
        client_id: process.env.VUE_APP_GOOGLE_ID,
        redirect_uri: process.env.VUE_APP_GOOGLE_CALLBACK,
        access_type: 'offline',
        response_type: 'code',
        prompt: 'consent',
        scope: 'https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/yt-analytics.readonly',
        hl: 'en'
      }
      window.open(`${rootUrl}?${new URLSearchParams(options).toString()}`, '_blank')
      this.oauthOpened = true
    },
    addYoutubeManual () {
      let username = this.manualYoutube.username.trim().replace(/^@/, '')
      if (!username || !this.manualYoutube.followers) return

      // Si pegaron una URL de YouTube, extraer el handle
      if (username.includes('youtube.com')) {
        const result = getUsernameUrlYoutube(username)
        username = result[0]
        if (!username) {
          this.youtubeChannelError = 'No se pudo extraer el handle de esa URL. Verifica e intenta de nuevo.'
          return
        }
      }

      this.youtubeChannelError = null
      this.savingManual = true
      const followers = parseInt(String(this.manualYoutube.followers).replace(/,/g, ''), 10)
      othersService.addSocialNetwork({ network: 'youtube', username, followers })
        .then(response => {
          if (response && response.code === 'accountNetwrokExists') {
            showToast(this, 'Cuenta ya registrada', 'Esta cuenta de YouTube ya está conectada.', 'AlertCircleIcon')
          } else if (response && response.code === 'youtube.channelDoesntExist') {
            this.youtubeChannelError = `No se encontró el canal "@${username}". Verifica el nombre de usuario e intenta de nuevo.`
          } else if (response) {
            this.addingManual = false
            this.manualYoutube = { username: '', followers: '' }
            this.youtubeChannelError = null
            this.youtubeNetworks = []
            this.fetchYoutubeNetworks()
          } else {
            this.youtubeChannelError = 'No se pudo agregar la cuenta. Intenta de nuevo.'
          }
        })
        .catch(() => {
          this.youtubeChannelError = 'No se pudo agregar la cuenta. Intenta de nuevo.'
        })
        .finally(() => {
          this.savingManual = false
        })
    },
    disabledAddCampaign () {
      return !hasPermission('add_campaign')
    },

    handleEnterKey (event) {
      const active = document.activeElement
      // Dejar que Enter funcione normal dentro del editor Quill
      if (active && (active.classList.contains('ql-editor') || active.closest('.ql-editor'))) return
      // Dejar que Enter funcione en dropdowns de v-select
      if (active && active.closest('.vs__dropdown-menu')) return
      // En el formulario manual de YouTube, Enter dispara "Agregar cuenta" en lugar de avanzar
      if (this.currentStep === 2 && this.addingManual) {
        event.preventDefault()
        this.addYoutubeManual()
        return
      }

      event.preventDefault()
      if (this.currentStep < 4) {
        this.validateAndNext()
      } else {
        this.createdList()
      }
    }
  }
}
</script>

<style lang="scss">
/* Dropdown appended to body debe quedar sobre el modal (z-index 1050) */
.vs__dropdown-menu {
  z-index: 1060 !important;
}
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';

/* ── Overlay full width ─────────────────────────────── */
.wiz-overlay {
  display: block !important;
  width: 100%;
  text-align: left;
}

/* ── Step indicator bar ─────────────────────────────── */
.wiz-steps {
  display: flex;
  border-bottom: 1px solid #ebe9f1;
}
.wiz-step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-size: 12px;
  color: #b9b9c3;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;

  &--active {
    color: #7367f0;
    border-bottom-color: #7367f0;
  }
  &--done {
    color: #28c76f;
    border-bottom-color: #28c76f;
  }
  &--clickable {
    cursor: pointer;
    &:hover {
      background: rgba(40, 199, 111, 0.05);
    }
  }
  &__num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1.5px solid currentColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
    transition: background 0.2s;
  }
  &--done &__num {
    background: rgba(40, 199, 111, 0.1);
  }
  &__label {
    font-weight: 500;
    white-space: nowrap;
    @media (max-width: 480px) {
      display: none;
    }
  }
}

/* ── Panel ──────────────────────────────────────────── */
.wiz-panel {
  padding: 1.25rem 1.5rem 0.5rem;
  min-height: 330px;
}
.wiz-label {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  text-align: left;
}

/* ── Cover drag-drop zone ───────────────────────────── */
.cover-zone {
  position: relative;
  border: 2px dashed #d8d6de;
  border-radius: 10px;
  overflow: hidden;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  margin-bottom: 0;

  &:hover:not(.cover-zone--filled) {
    border-color: #7367f0;
    background: rgba(115, 103, 240, 0.04);
  }
  &--over {
    border-color: #7367f0 !important;
    background: rgba(115, 103, 240, 0.08) !important;
  }
  &--filled {
    border-style: solid;
    border-color: #ebe9f1;
    cursor: default;
  }
  &__hint {
    text-align: center;
    padding: 1.5rem;
    pointer-events: none;
    a { pointer-events: all; }
  }
  &__img {
    width: 100%;
    height: 140px;
    object-fit: cover;
    display: block;
  }
  &__change {
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 12px;
  }
}

/* ── Logo circle ────────────────────────────────────── */
.logo-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -28px;
  margin-bottom: 14px;
  position: relative;
  z-index: 2;
}
.logo-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
  &__over {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.38);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    opacity: 0;
    transition: opacity 0.15s;
  }
  &:hover &__over {
    opacity: 1;
  }
}

/* ── Two-column grid ────────────────────────────────── */
.wiz-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

/* ── Quill editor ───────────────────────────────────── */
.wiz-quill {
  ::v-deep > .ql-container {
    border-bottom: 0;
    min-height: 130px;
  }
  + [id^=quill-toolbar] {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
  }
}

/* ── Guideline cards ────────────────────────────────── */
.wiz-guideline {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 10px 12px;
}

/* ── Navigation footer ──────────────────────────────── */
.wiz-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 1.5rem 1.25rem;
  border-top: 1px solid #ebe9f1;
  margin-top: 4px;
}

/* ── Progress dots ──────────────────────────────────── */
.wiz-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}
.wiz-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d8d6de;
  transition: width 0.2s, background 0.2s, border-radius 0.2s;

  &--active {
    background: #7367f0;
    width: 20px;
    border-radius: 4px;
  }
  &--done {
    background: #28c76f;
  }
}

/* ── Google OAuth button ────────────────────────────── */
.google-yt-btn {
  background: #fff !important;
  border: 1px solid #dadce0 !important;
  color: #3c4043 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 40px;
  font-weight: 500;
  box-shadow: none !important;

  &:hover {
    background: #f8f9fa !important;
  }

  &__icon {
    width: 18px;
    height: 18px;
  }
}

/* ── Divider ─────────────────────────────────────────── */
.yt-divider {
  position: relative;
  border-top: 1px solid #ebe9f1;
  text-align: center;

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    padding: 0 8px;
    font-size: 12px;
    color: #b9b9c3;
  }
}

/* ── YouTube network cards ──────────────────────────── */
.youtube-networks-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.youtube-network-card {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 1.5px solid #ebe9f1;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #c4302b;
    background: rgba(196, 48, 43, 0.04);
  }

  &--selected {
    border-color: #c4302b;
    background: rgba(196, 48, 43, 0.06);
  }
}
</style>
